import { once, run } from '../helper/electron';
import isElectron from './isElectron';

export type SeratoVersionPreference = 'auto' | 'serato3' | 'serato4';
export type SeratoExportTarget = 'legacy' | 'serato4';

export interface SeratoExportTargetPreview {
  preference: SeratoVersionPreference;
  needsChoice: boolean;
  target?: SeratoExportTarget;
}

export async function fetchSeratoExportVersionSetting(): Promise<SeratoVersionPreference> {
  if (!isElectron()) {
    return 'auto';
  }

  return new Promise((resolve) => {
    once(
      'serato/get-export-version-setting',
      (response: { success?: boolean; preference?: SeratoVersionPreference }) => {
        if (
          response?.success &&
          (response.preference === 'auto' ||
            response.preference === 'serato3' ||
            response.preference === 'serato4')
        ) {
          resolve(response.preference);
          return;
        }
        resolve('auto');
      },
    );
    try {
      run('serato/get-export-version-setting');
    } catch {
      resolve('auto');
    }
  });
}

export async function saveSeratoExportVersionSetting(
  preference: SeratoVersionPreference,
): Promise<boolean> {
  if (!isElectron()) {
    return false;
  }

  return new Promise((resolve) => {
    once(
      'serato/set-export-version-setting',
      (response: { success?: boolean }) => {
        resolve(!!response?.success);
      },
    );
    try {
      run('serato/set-export-version-setting', { preference });
    } catch {
      resolve(false);
    }
  });
}

export async function previewSeratoExportTarget(): Promise<SeratoExportTargetPreview | null> {
  if (!isElectron()) {
    return null;
  }

  return new Promise((resolve) => {
    once(
      'serato/preview-export-target',
      (response: {
        success?: boolean;
        preference?: SeratoVersionPreference;
        needsChoice?: boolean;
        target?: SeratoExportTarget;
      }) => {
        if (!response?.success || !response.preference) {
          resolve(null);
          return;
        }
        resolve({
          preference: response.preference,
          needsChoice: !!response.needsChoice,
          target: response.target,
        });
      },
    );
    try {
      run('serato/preview-export-target');
    } catch {
      resolve(null);
    }
  });
}

export function seratoMenuTargetFromPreference(
  preference: SeratoVersionPreference,
): SeratoExportTarget | undefined {
  if (preference === 'serato3') {
    return 'legacy';
  }
  if (preference === 'serato4') {
    return 'serato4';
  }
  return undefined;
}

export function preferenceFromSeratoMenuTarget(
  target: SeratoExportTarget,
): Exclude<SeratoVersionPreference, 'auto'> {
  return target === 'legacy' ? 'serato3' : 'serato4';
}
